import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Phone, User, CheckCheck, MessageCircle, Instagram, Send, CalendarClock } from "lucide-react";
import { getNotifications, markNotificationRead, markAllNotificationsRead, getAppointmentReminder } from "@/lib/dentiumApi";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

const CH_ICON = { whatsapp: MessageCircle, instagram: Instagram, telegram: Send };
const CH_COLOR = { whatsapp: "#25D366", instagram: "#E1306C", telegram: "#2AABEE" };

const timeAgo = (iso) => {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 60000;
    if (diff < 1) return "ahora";
    if (diff < 60) return `hace ${Math.round(diff)} min`;
    if (diff < 1440) return `hace ${Math.round(diff / 60)} h`;
    return `hace ${Math.round(diff / 1440)} d`;
  } catch {
    return "";
  }
};

export const NotificationBell = () => {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: getNotifications,
    refetchInterval: 5000,
  });
  const unread = data?.unread ?? 0;
  const items = data?.items ?? [];

  const { data: reminder } = useQuery({
    queryKey: ["appointment-reminder"],
    queryFn: getAppointmentReminder,
    refetchInterval: 60000,
  });
  const highlight = reminder?.active || reminder?.soon;

  const reminderText = () => {
    if (!reminder) return "";
    if (reminder.active) return `Turno de citas ACTIVO ahora · ${reminder.label}`;
    if (reminder.soon) {
      const m = reminder.minutes_until;
      return `Próximo turno en ${m} min · ${reminder.label}`;
    }
    return "";
  };

  const readOne = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const readAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className={`relative h-9 w-9 rounded-full border flex items-center justify-center transition-colors ${
            highlight
              ? "border-amber-300 bg-amber-50"
              : "border-[#E7E2DA] bg-white hover:bg-[#FAF8F5]"
          }`}
        >
          {highlight && (
            <span className="absolute inset-0 rounded-full ring-2 ring-amber-400/60 animate-ping" />
          )}
          <Bell className={`h-4.5 w-4.5 ${highlight ? "text-amber-600" : "text-[#6B6157]"}`} />
          <AnimatePresence>
            {unread > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                data-testid="notification-badge"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
              >
                {unread}
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0" data-testid="notification-panel">
        {highlight && (
          <div
            data-testid="reminder-banner"
            className={`flex items-center gap-2 px-3.5 py-2.5 text-xs font-semibold ${
              reminder?.active ? "bg-emerald-500 text-white" : "bg-amber-400 text-amber-950"
            }`}
          >
            <CalendarClock className="h-4 w-4 shrink-0" />
            {reminderText()}
          </div>
        )}
        <div className="flex items-center justify-between p-3.5 border-b border-[#EDE7DE]">
          <div>
            <h3 className="text-sm font-bold text-[#1A1A1A]">Avisos del equipo</h3>
            <p className="text-[11px] text-[#A79E90]">Contactos recogidos fuera de horario</p>
          </div>
          {unread > 0 && (
            <Button
              size="sm"
              variant="ghost"
              data-testid="notification-mark-all"
              onClick={() => readAll.mutate()}
              className="h-7 text-[11px] text-[#1A1A1A]"
            >
              <CheckCheck className="h-3.5 w-3.5 mr-1" /> Marcar leídos
            </Button>
          )}
        </div>

        <div className="max-h-[360px] overflow-y-auto dentium-scroll">
          {items.length === 0 && (
            <div className="p-8 text-center text-xs text-[#A79E90]">Sin avisos pendientes.</div>
          )}
          {items.map((n) => {
            const Icon = CH_ICON[n.channel];
            return (
              <button
                key={n.id}
                data-testid="notification-item"
                onClick={() => !n.read && readOne.mutate(n.id)}
                className={`w-full text-left p-3.5 border-b border-slate-50 flex gap-3 hover:bg-[#FAF8F5] transition-colors ${
                  n.read ? "opacity-60" : "bg-amber-50/40"
                }`}
              >
                <span
                  className="h-9 w-9 rounded-full flex items-center justify-center text-white shrink-0"
                  style={{ background: CH_COLOR[n.channel] }}
                >
                  <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-bold text-[#2A2723] truncate flex items-center gap-1">
                      <User className="h-3.5 w-3.5 text-[#A79E90]" /> {n.name}
                    </span>
                    {!n.read && <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" />}
                  </div>
                  <div className="text-xs text-[#8A7E70] flex items-center gap-1 mt-0.5">
                    <Phone className="h-3 w-3 text-amber-600" /> {n.phone}
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] font-medium text-[#1A1A1A] bg-[#F1EBE1] rounded px-1.5 py-0.5">{n.topic}</span>
                    <span className="text-[10px] text-[#A79E90]">{timeAgo(n.created_at)}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
